/**
 * @fileoverview AR-805 — per-project Classroom link state + mutations, used by
 * ProjectClassroomCard. Mirrors the prototype's ProjectClassroomCard.tsx state
 * (LinkState + one-at-a-time BusyAction locking) over the ProjectLinkPort.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import { getClassroomAdapters } from '../provider'
import { ProjectLinkResult, ProjectLinkState } from '../types'
import { useRefreshOnFocus } from './useRefreshOnFocus'

export type ProjectLinkBusyAction = 'linkExisting' | 'toggle' | 'unlink' | 'syncRoster'

export interface ProjectClassroomLinkState {
  state: ProjectLinkState | null
  loading: boolean
  busy: ProjectLinkBusyAction | null
  refresh: () => Promise<void>
  linkCourse: (courseId: string) => Promise<ProjectLinkResult>
  syncRoster: () => Promise<ProjectLinkResult>
  setSyncEnabled: (enabled: boolean) => Promise<void>
  unlink: () => Promise<void>
}

export function useProjectClassroomLink(projectId: string): ProjectClassroomLinkState {
  const { projectLink } = getClassroomAdapters()
  const [state, setState] = useState<ProjectLinkState | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<ProjectLinkBusyAction | null>(null)

  // Monotonic token: a late response from an earlier refresh must never
  // overwrite a newer one (e.g. mount + immediate mutation).
  const generation = useRef(0)

  const refresh = useCallback(async () => {
    const current = ++generation.current
    try {
      const next = await projectLink.getLink(projectId)
      if (generation.current === current) setState(next)
    } catch {
      if (generation.current === current) setState(null)
    } finally {
      if (generation.current === current) setLoading(false)
    }
  }, [projectLink, projectId])

  useEffect(() => {
    refresh()
    // Ignore in-flight results after unmount: bumping the generation makes
    // every pending refresh a no-op.
    return () => {
      generation.current += 1
    }
  }, [refresh])

  // The teacher leaves for classroom.google.com and comes back — pick up the
  // new state without asking for a reload.
  useRefreshOnFocus(refresh)

  const runBusy = useCallback(
    async <T>(action: ProjectLinkBusyAction, run: () => Promise<T>): Promise<T> => {
      setBusy(action)
      try {
        return await run()
      } finally {
        // Refresh on failure too: a rejected mutation (e.g. already_linked)
        // usually means the card is showing stale state.
        setBusy(null)
        await refresh()
      }
    },
    [refresh],
  )

  return {
    state,
    loading,
    busy,
    refresh,
    linkCourse: useCallback(
      (courseId: string) => runBusy('linkExisting', () => projectLink.linkCourse(projectId, courseId)),
      [runBusy, projectLink, projectId],
    ),
    syncRoster: useCallback(
      () => runBusy('syncRoster', () => projectLink.syncRoster(projectId)),
      [runBusy, projectLink, projectId],
    ),
    setSyncEnabled: useCallback(
      async (enabled: boolean) => {
        await runBusy('toggle', () => projectLink.setSyncEnabled(projectId, enabled))
      },
      [runBusy, projectLink, projectId],
    ),
    unlink: useCallback(
      () => runBusy('unlink', () => projectLink.unlink(projectId)),
      [runBusy, projectLink, projectId],
    ),
  }
}
